import React, { type ReactNode, lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { FormProvider, useForm, useFormState, type Validate } from 'react-hook-form-latest';
import { ErrorMessage } from '@hookform/error-message';

import { _isEmpty, _isEmptyString, _isNil, _isNotEmpty, _notNil } from '@/littledash';

import type { MetadataField, MetadataFieldValueTypes, MetadataFieldWithValue } from '@/model/Metadata.model';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import type { StudyApiId } from '@/model/Study.model';
import { type ID } from '@/model/Common.model';
import { type SpeciesV1 } from '@/model/Species.model';
import { AnimalSexLabelToCode } from '@/model/Animal.utils';
import { AnimalSexMap, type AnimalSexLabel } from '@/model/Animal.model';
import {
  type AnimalCreateV1,
  type AnimalDefinitionApiId,
  type AnimalDefinitionUpsertV1,
  PlanTypeMap,
} from '@/model/PlannedAnimal.model';

import type { CustomGridSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import type { SpreadsheetV16Settings } from '@/components/UI/SpreadSheet/SpreadsheetV16';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import { featuresSelector } from '@/support/Selectors';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';
import { notAborted } from '@/support/Hooks/fetch/useAbortController';
import { useEntitySchemaFieldsByType } from '@/utils/entitySchema/useEntitySchema';
import { generateApiId } from '@/utils/ApiId.utils';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';
import { errorToast, preventNumberScroll } from '@/helpers';
import { metadataToColumnSettingMapper } from '@/components/CreateTreatmentGroups/CreateTreatmentGroups.utils';

import {
  type AnimalRow,
  generateInitialAnimals,
  handleMetadataChange,
  mapMetadataResponse,
  type MetadataGlossaryItem,
  patchMetadata,
  getColumnData,
} from '@/components/Studies/Create/Steps/Animals/Animals.utils';
import { getColumnDataV16, patchMetadataV16 } from '@/components/Studies/Create/Steps/Animals/Animals.utils.v16';
import { formatColumnSettingsDataValueV16, sanitizeMetadataValueV16, valueSetV16 } from '@/support/hot-v16';
import { sanitizeMetadataValue, valueSet } from '@/support/hot';

import type { PlannedAnimalDefinition } from './PlannedAnimals/PlannedAnimals.utils';
import Loading from '@/components/Loading';
import Button from '@/components/UI/Button';
import ApiErrorBanner from '@/components/ApiErrorBanner';
import './PlannedAnimals.scss';

import type HotTable from '@handsontable//react';
import cn from 'classnames';

const SpreadSheet = lazy(() => import('@/components/UI/SpreadSheet'));
const CreateAnimalsTableV16 = lazy(() => import('./CreateAnimalsTableV16'));

interface CreateAnimalsProps {
  plannedAnimals?: Array<PlannedAnimalDefinition>;
  selectedIds?: Set<AnimalDefinitionApiId>;
  studyId?: ID;
  studyApiId?: StudyApiId;
  onSubmit: (animals: Array<AnimalDefinitionUpsertV1> | AnimalCreateV1) => Promise<void>;
  editing?: boolean;
}
interface AnimalsPerCageData {
  animalsPerCage: number | null;
}
const animalsPerCageValidation: Validate<AnimalsPerCageData['animalsPerCage'], AnimalsPerCageData> = (value) => {
  if (_isNil(value) || !Number.isFinite(value)) {
    return 'Must be a valid number';
  }
  if (value <= 0) {
    return 'Must be greater than 0';
  }
  if (value > 15) {
    return 'Maximum of 15 animals per cage';
  }
  return true;
};
const groupIdAccessor = (group: TreatmentGroup) => group.api_id as string;

const hotUpgradeSelector = createSelector([featuresSelector], (features) => ({
  hotUpgrade: features?.hot_upgrade ?? false,
  unknownSexEnabled: features?.unknown_sex === true,
}));

const EMPTY_UNSELECTED: Array<PlannedAnimalDefinition> = [];

const CreateAnimals: React.FC<CreateAnimalsProps> = ({
  plannedAnimals: initialPlannedAnimals,
  selectedIds: initialSelectedIds,
  studyId,
  studyApiId,
  onSubmit,
  editing = false,
}) => {
  const { hotUpgrade, unknownSexEnabled } = useSelector(hotUpgradeSelector);

  const [tableValid, setTableValid] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [apiError, setApiError] = useState<unknown | false>(false);
  const [selectedMetadata, setSelectedMetadata] = useState<Array<MetadataField>>([]);
  const [availableMetadata, setAvailableMetadata] = useState<Array<MetadataField>>([]);
  const [plannedAnimals, setPlannedAnimals] = useState<Array<PlannedAnimalDefinition>>(initialPlannedAnimals ?? []);
  const [selectedIds, setSelectedIds] = useState<Set<AnimalDefinitionApiId>>(initialSelectedIds ?? new Set());
  const [groups, setGroups] = useState<Array<TreatmentGroup>>([]);
  const [species, setSpecies] = useState<Array<SpeciesV1>>([]);
  const [studyMetadata, setStudyMetadata] = useState<Array<MetadataField>>([]);

  const entityFieldRequiredMap = useEntitySchemaFieldsByType('animal');
  const dobRequired = entityFieldRequiredMap?.dob ?? true;

  const refV12 = useRef<HotTable>(null);
  const refV16 = useRef<HotTableRef>(null);
  const dispatch = useDispatch();

  const { invoke: fetchSpecies } = useApiHook({
    endpoint: 'GET /api/v1/species',
    invokeOnInit: false,
    query: { include: ['strains'] },
    options: { onError: { slug: 'fetch-species-data', throw: false, capture: true, toast: false } },
  });

  const { invoke: fetchGroups } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.groups.list',
    path: { id: studyId },
    query: { perPage: -1, include: ['animals'] },
    options: { invokeOnInit: false, onError: { slug: 'fetch-study-groups', throw: false } },
  });

  const { invoke: fetchMetadata } = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: {
      include_inactive: false,
      filter_type: 'subject_meta',
    },
    invokeOnInit: false,
    options: { onError: { slug: 'fetch-metadata-options', throw: false, capture: true, toast: false } },
  });

  const { invoke: fetchPlannedAnimals } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/planned/animals',
    path: { studyApiId: studyApiId as StudyApiId },
    query: { perPage: -1 },
    invokeOnInit: false,
    options: { onError: { slug: 'fetch-planned-animals', throw: false, capture: true, toast: false } },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [groupsResponse, speciesResponse, metadataResponse, plannedAnimalsResponse] = await Promise.all([
          fetchGroups(),
          fetchSpecies(),
          fetchMetadata(),
          _isEmpty(initialPlannedAnimals) ? fetchPlannedAnimals() : Promise.resolve(),
        ]);
        if (speciesResponse.type === 'success') {
          setSpecies(speciesResponse.body.data ?? []);
        }
        if (groupsResponse.type === 'success') {
          const groupsData = ((groupsResponse?.body as { data: Array<TreatmentGroup> })?.data ?? []).map((g) => ({
            ...g,
            animals_count: g.animals?.length ?? 0,
          }));
          setGroups(groupsData);
        }
        if (metadataResponse.type === 'success') {
          const metadata = mapMetadataResponse((metadataResponse?.body || []) as Array<MetadataGlossaryItem>);
          setStudyMetadata(metadata);
        }
        if (_isEmpty(initialPlannedAnimals) && _notNil(studyApiId)) {
          if (plannedAnimalsResponse?.type === 'success') {
            const animals = (plannedAnimalsResponse?.body?.data ?? []) as unknown as Array<PlannedAnimalDefinition>;
            setPlannedAnimals(animals);
            setSelectedIds(new Set(animals?.map((a) => a.api_id)));
          }
        }
      } catch (error) {
        if (notAborted(error)) {
          setApiError(error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const formMethods = useForm<AnimalsPerCageData>({
    defaultValues: {
      animalsPerCage: 5,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  const {
    register,
    formState: { errors },
  } = formMethods;
  const { isValid: cageValid } = useFormState({ control: formMethods.control, name: 'animalsPerCage' });

  const unmappedPlannedAnimals = useMemo(() => {
    return plannedAnimals.filter((animal) => _isNil(animal.animal_api_id));
  }, [plannedAnimals]);
  const selectedAnimals: Array<PlannedAnimalDefinition> = useMemo(() => {
    return (unmappedPlannedAnimals ?? []).filter((animal) => selectedIds.has(animal.api_id));
  }, [unmappedPlannedAnimals, selectedIds]);

  useEffect(() => {
    if (_isNotEmpty(studyMetadata)) {
      const populatedMetadata =
        selectedAnimals?.reduce((acc, animal) => {
          animal.metadata.forEach((meta) => {
            const metadataField = studyMetadata.find((m) => m.glossary_api_id === meta.type);
            if (_notNil(metadataField) && !acc.some((m) => m.id === metadataField.id)) {
              acc.push(metadataField);
            }
          });
          return acc;
        }, [] as Array<MetadataField>) ?? [];
      setSelectedMetadata(populatedMetadata);
      const remainingMetadata = studyMetadata.filter((meta) => {
        return !populatedMetadata.some((existingMeta) => existingMeta.title === meta.title);
      });
      setAvailableMetadata(remainingMetadata);
    }
  }, [selectedAnimals, studyMetadata]);

  const animalData = useMemo<Array<AnimalRow>>(() => {
    if (_isNotEmpty(selectedAnimals) && _isNotEmpty(groups)) {
      return selectedAnimals.reduce<Array<AnimalRow>>((acc, animal) => {
        if (_notNil(animal.animal_api_id)) {
          return acc as Array<AnimalRow>;
        }

        const group = groups.find((g) => g.api_id === animal.study_group_api_id);
        const speciesItem = species?.find((s) => s.api_id === animal.species_api_id);
        const strainItem = speciesItem?.strains?.find((strain) => strain.api_id === animal.strain_api_id);

        const metadataObj: Record<string, MetadataFieldWithValue> = {};
        const metaV16: Record<string, unknown> = {};
        animal.metadata.forEach((meta) => {
          const metadataField = studyMetadata.find((m) => m.glossary_api_id === meta.type);
          if (_notNil(metadataField)) {
            metadataObj[metadataField.id] = {
              id: metadataField.id,
              title: metadataField.title,
              value: meta.value,
              glossary_id: metadataField.glossary_id,
              glossary_api_id: metadataField.glossary_api_id,
              field_type: metadataField.field_type,
              read_only: metadataField.read_only,
              slug: metadataField.slug,
              required: metadataField.required,
              created_at: metadataField.created_at,
              updated_at: metadataField.updated_at,
            };
            if (hotUpgrade && _notNil(meta.value)) {
              const encodedKey = formatColumnSettingsDataValueV16(metadataField.title);
              const isMulti =
                metadataField.field_type === 'multi_select' || metadataField.field_type === 'lookup_multi_select';
              metaV16[encodedKey] = isMulti && Array.isArray(meta.value) ? meta.value.join(',') : meta.value;
            }
          }
        });

        const row: Record<string, unknown> = {
          plannedId: animal.planned_id,
          apiId: animal.api_id,
          groupIdentifier: group?.api_id,
          sex: AnimalSexMap[animal.sex],
          species: speciesItem?.name ?? '',
          strain: strainItem?.name ?? '',
          metadata: metadataObj,
          isMapped: _notNil(animal.animal_api_id),
        };
        if (hotUpgrade) {
          row.meta = metaV16;
        }
        acc.push(row as unknown as AnimalRow);

        return acc;
      }, [] as Array<AnimalRow>);
    }
    if (_isEmpty(selectedAnimals) && _isNotEmpty(groups) && !editing) {
      return generateInitialAnimals(groups, dobRequired);
    }
    return [];
  }, [selectedAnimals, groups, species, studyMetadata, dobRequired, editing]);

  const unselectedPlannedAnimals = useMemo(() => {
    return plannedAnimals.filter((a) => !selectedIds.has(a.api_id) || _notNil(a.animal_api_id));
  }, [plannedAnimals, selectedIds]);

  const planType = editing ? PlanTypeMap.planned : PlanTypeMap.registered;
  const unselectedForValidation = editing ? unselectedPlannedAnimals : EMPTY_UNSELECTED;

  const columnDataForTable = useMemo(() => {
    if (hotUpgrade) {
      return getColumnDataV16(
        species,
        selectedMetadata,
        groups,
        groupIdAccessor,
        planType,
        editing,
        dobRequired,
        unselectedForValidation,
        unknownSexEnabled
      );
    }
    return getColumnData(
      species,
      selectedMetadata,
      groups,
      groupIdAccessor,
      refV12.current?.hotInstance ?? null,
      planType,
      editing,
      dobRequired,
      unselectedForValidation,
      unknownSexEnabled
    );
  }, [
    species,
    selectedMetadata,
    groups,
    editing,
    dobRequired,
    unselectedForValidation,
    hotUpgrade,
    planType,
    unknownSexEnabled,
  ]);

  useEffect(() => {
    const hotInstance = hotUpgrade ? refV16.current?.hotInstance : refV12.current?.hotInstance;
    if (
      _notNil(hotInstance) &&
      _isNotEmpty(animalData) &&
      !(hotInstance as unknown as { isDestroyed: boolean }).isDestroyed
    ) {
      window.requestAnimationFrame(() => hotInstance.validateCells((valid: boolean) => setTableValid(valid)));
    }
  }, [animalData, columnDataForTable, hotUpgrade]);

  const settingsV12 = useMemo<CustomGridSettings>(
    () => ({
      ...columnDataForTable,
      manualColumnResize: true,
      rowHeaders: true,
      outsideClickDeselects: true,
      allowInsertRow: true,
      allowInsertColumn: false,
      allowRemoveRow: false,
      minSpareRows: 0,
      columnSorting: false,
      className: 'htMiddle',
      rowHeights: 50,
      height: editing ? 'calc( 90vh - 250px)' : 'calc( 90vh - 300px)',
    }),
    [columnDataForTable, editing]
  );

  const settingsV16 = useMemo<SpreadsheetV16Settings>(
    () => ({
      ...columnDataForTable,
      manualColumnResize: true,
      rowHeaders: true,
      outsideClickDeselects: true,
      minSpareRows: 0,
      columnSorting: false,
      className: 'htMiddle',
      rowHeights: 50,
      height: editing ? 'calc( 90vh - 250px)' : 'calc( 90vh - 300px)',
    }),
    [columnDataForTable, editing]
  );

  const closeModal = () => {
    dispatch({
      type: 'CLOSE_MODAL',
    });
  };

  const afterChangeIncludeSources = useMemo(
    () => new Set(['edit', 'Autofill.fill', 'CopyPaste.paste', 'UndoRedo.undo', 'UndoRedo.redo']),
    []
  );

  const handleDataChange = useCallback(
    (_changes?: unknown, source?: string) => {
      if (hotUpgrade && _notNil(source) && !afterChangeIncludeSources.has(source)) {
        return;
      }
      const hotInstance = hotUpgrade ? refV16.current?.hotInstance : refV12.current?.hotInstance;
      if (_notNil(hotInstance) && !(hotInstance as unknown as { isDestroyed: boolean }).isDestroyed) {
        window.requestAnimationFrame(() => {
          const inst = hotUpgrade ? refV16.current?.hotInstance : refV12.current?.hotInstance;
          if (_notNil(inst) && !(inst as unknown as { isDestroyed: boolean }).isDestroyed) {
            inst.validateCells((valid: boolean) => setTableValid(valid));
          }
        });
      }
    },
    [setTableValid, hotUpgrade, afterChangeIncludeSources]
  );
  const formValid = cageValid && tableValid;
  const patchMetadataCallback = useCallback(() => {
    if (hotUpgrade) {
      patchMetadataV16(refV16.current?.hotInstance ?? null, species);
    } else {
      patchMetadata(refV12.current?.hotInstance ?? null, settingsV12, species);
    }
  }, [species, settingsV12, hotUpgrade]);

  const handleMetadataChangeCallback = useCallback(
    (metadata: Array<MetadataField>) => {
      handleMetadataChange(metadata, studyMetadata, setSelectedMetadata, setAvailableMetadata);
    },
    [studyMetadata, setSelectedMetadata, setAvailableMetadata]
  );

  const submitAnimals = async () => {
    if (!formValid || isSubmitting || !_notNil(onSubmit)) {
      return;
    }

    const hotInstance = hotUpgrade ? (refV16.current?.hotInstance ?? null) : (refV12.current?.hotInstance ?? null);
    if (_notNil(hotInstance)) {
      const valid = await new Promise<boolean>((resolve) => hotInstance.validateCells(resolve));
      if (!valid) {
        setTableValid(false);
        return;
      }
    }

    try {
      setIsSubmitting(true);
      const currentData = (hotInstance?.getSourceData() as Array<AnimalRow>) ?? [];

      const animalsToCreate = currentData
        .filter((animal) => !(animal.isMapped ?? false))
        .map((animal) => {
          const group = groups.find((g) => g.api_id === animal.groupIdentifier);
          const speciesItem = species.find((s) => s.name === animal.species);
          const strainItem = speciesItem?.strains?.find((strain) => strain.name === animal.strain);

          const metadataArray = hotUpgrade
            ? selectedMetadata.reduce<Array<{ type: string; value: MetadataFieldValueTypes }>>((acc, field) => {
                const encodedKey = formatColumnSettingsDataValueV16(field.title);
                const metaRecord = (animal as unknown as Record<string, Record<string, unknown>>).meta;
                const rawValue = metaRecord?.[encodedKey];
                if (_notNil(field.glossary_api_id) && _notNil(rawValue)) {
                  const isMulti = field.field_type === 'multi_select' || field.field_type === 'lookup_multi_select';
                  const value = isMulti
                    ? typeof rawValue === 'string'
                      ? rawValue.split(',').map((v: string) => v.trim())
                      : rawValue
                    : rawValue;
                  const sanitized = sanitizeMetadataValueV16(value as MetadataFieldValueTypes, field.field_type);
                  if (valueSetV16(sanitized)) {
                    acc.push({ type: field.glossary_api_id, value: sanitized });
                  }
                }
                return acc;
              }, [])
            : Object.values(animal.metadata ?? {}).reduce<Array<{ type: string; value: MetadataFieldValueTypes }>>(
                (acc, meta) => {
                  if (_notNil(meta.glossary_api_id)) {
                    const sanitizedValue = sanitizeMetadataValue(meta.value, meta.field_type);
                    if (valueSet(sanitizedValue)) {
                      acc.push({
                        type: meta.glossary_api_id,
                        value: sanitizedValue,
                      });
                    }
                  }
                  return acc;
                },
                []
              );

          if (editing) {
            return {
              planned_id: animal.plannedId,
              api_id: animal?.apiId,
              study_group_api_id: group?.api_id,
              sex: AnimalSexLabelToCode[animal.sex as AnimalSexLabel] ?? animal.sex,
              species_api_id: speciesItem?.api_id,
              strain_api_id: strainItem?.api_id,
              metadata: metadataArray,
            };
          } else {
            return {
              api_id: animal?.apiId ?? generateApiId('adf'),
              overrides: {
                name: animal.plannedId,
                sex: AnimalSexLabelToCode[animal.sex as AnimalSexLabel] ?? animal.sex,
                dob: animal.dob,
                strain_api_id: strainItem?.api_id,
                metadata: metadataArray,
              },
            };
          }
        });

      if (editing) {
        await onSubmit(animalsToCreate as Array<AnimalDefinitionUpsertV1>);
      } else {
        await onSubmit({
          cage_animal_count: formMethods.getValues('animalsPerCage'),
          definitions: animalsToCreate,
        } as AnimalCreateV1);
      }
      closeModal();
    } catch (err) {
      if (editing) {
        errorToast('Could not submit edit animals request');
      } else {
        errorToast('Could not submit create animals request');
      }
      ExceptionHandler.captureException(
        new InVivoError('Could not create animals', {
          cause: err,
          slug: 'create-animals-modal-error',
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleValidationErrorMessage = (isValid: boolean, value: ReactNode, row: number, prop: string | number) => {
    if (!isValid) {
      if (prop === 'groupIdentifier') {
        const hot = hotUpgrade ? refV16.current?.hotInstance : refV12.current?.hotInstance;
        if (_notNil(hot) && !_isEmptyString(value)) {
          const group = groups.find((g) => g.api_id === value);
          if (_notNil(group)) {
            if (editing) {
              const errorMsg =
                'Number of planned animals assigned exceeds maximum subjects allowed: ' + (group?.max_subjects ?? 0);
              hot.getCell(row, hot.propToCol('groupIdentifier'))?.setAttribute('data-tooltip-content', errorMsg);
            } else {
              const availableCapacity = (group?.max_subjects ?? 0) - (group?.animals_count ?? 0);
              const errorMsg = 'Available group slots to register animals: ' + availableCapacity;
              hot.getCell(row, hot.propToCol('groupIdentifier'))?.setAttribute('data-tooltip-content', errorMsg);
            }
          } else {
            const errorMsg = 'Planned animals must be assigned to a group';
            hot.getCell(row, hot.propToCol('groupIdentifier'))?.setAttribute('data-tooltip-content', errorMsg);
          }
        }
      }
    }
  };

  if (_isEmpty(selectedAnimals)) {
    return (
      <div
        style={{ minHeight: '90vh' }}
        data-test-component="EmptyPlannedAnimals"
        data-test-element="container"
        className="flex flex-wrap w-90 overflow-hidden center ma3 bg-light-gray br2 mt4 mb-2 relative"
      >
        <div className="pa4">
          <div className="text-center">
            <h3 className="mb2">No planned animals found</h3>
            <p className="mb3">
              {_isEmpty(groups)
                ? 'There are no planned animals available to create.'
                : 'Cannot edit mapped planned animals'}
            </p>
            <Button plain onClick={closeModal}>
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ minHeight: '90vh' }}
      data-test-component="CreateAnimals"
      data-test-element="container"
      className="flex flex-wrap w-90 overflow-hidden center ma3 bg-light-gray br2 mt4 mb-2 relative"
    >
      {apiError ? (
        <ApiErrorBanner
          className="mw6 mb4"
          error={apiError}
          title="There was an error fetching animals data"
          text="An error has occurred when fetching animals data, please try again. If this keeps occurring please contact support."
        />
      ) : null}
      <div className="flex flex-wrap self-start w-100" style={{ minHeight: 'calc( 90vh - 72px)' }}>
        <div className="createAnimalsHeaderSection">
          <h3 className="normal f4 lh-title">
            {editing ? 'Edit planned animals' : 'Create animals from planned animals'}
          </h3>
          <p className="lh-copy f5 pt2">Review and edit the planned animals data before registering animals.</p>
        </div>
        {isLoading ? (
          <div className="w-100 h-100">
            <Loading txt="Loading data..." />
          </div>
        ) : (
          <div
            className={cn('ph4 pb3 w-100 ow-spreadsheet-styles', { 'ow-spreadsheet-styles-v16': hotUpgrade })}
            data-test-component="CreateAnimals"
            data-test-element="spreadsheet-container"
          >
            {!editing && (
              <FormProvider {...formMethods}>
                <div className="createAnimalsFormSection">
                  <form>
                    <label>Animals per Cage</label>
                    <input
                      {...register('animalsPerCage', {
                        required: 'Value required',
                        validate: animalsPerCageValidation,
                        valueAsNumber: true,
                      })}
                      type="number"
                      onWheel={preventNumberScroll}
                      style={{ marginBottom: 0, width: '150px' }}
                      className="mr2"
                      data-testid="generate-cage-count-input"
                    />
                    <ErrorMessage
                      errors={errors}
                      name={`animalsPerCage`}
                      render={({ message }) => <p className="f6 red pt1 w-90">{message}</p>}
                    />
                  </form>
                </div>
              </FormProvider>
            )}
            {hotUpgrade ? (
              <Suspense fallback={<Loading />}>
                <CreateAnimalsTableV16
                  data={animalData as unknown as Array<Record<string, unknown>>}
                  settings={settingsV16}
                  innerRef={refV16 as React.RefObject<HotTableRef>}
                  metadata={availableMetadata}
                  patchMetadataAfterSettingsUpdated={patchMetadataCallback}
                  setMetadata={handleMetadataChangeCallback}
                  maxRows={Math.max(
                    groups.reduce((acc, group) => acc + group.max_subjects, 0),
                    plannedAnimals.length
                  )}
                  afterChange={handleDataChange}
                  afterValidation={handleValidationErrorMessage}
                  buttonClasses="absolute-sheet-buttons-v16"
                />
              </Suspense>
            ) : (
              <Suspense fallback={<Loading />}>
                <SpreadSheet
                  data={animalData}
                  settings={settingsV12}
                  innerRef={refV12}
                  className="createAnimals"
                  buttonClasses="absolute-sheet-buttons"
                  metadata={availableMetadata}
                  metadataToColumnSettingMapper={metadataToColumnSettingMapper}
                  patchMetadataAfterSettingsUpdated={patchMetadataCallback}
                  setMetadata={handleMetadataChangeCallback}
                  maxRows={Math.max(
                    groups.reduce((acc, group) => acc + group.max_subjects, 0),
                    plannedAnimals.length
                  )}
                  afterChange={handleDataChange}
                  afterValidation={handleValidationErrorMessage}
                />
              </Suspense>
            )}
          </div>
        )}
      </div>
      <div
        className="ph4 pv3 flex self-end bt b--moon-gray w-100"
        data-test-component="CreateAnimals"
        data-test-element="action-container"
      >
        <Button
          onClick={submitAnimals}
          testId="save-action"
          disabled={!formValid || isSubmitting}
          tooltip={formValid ? undefined : spreadSheetValidationTooltip}
        >
          {editing ? 'Save' : 'Create Animals'}
        </Button>
        <Button plain className="ml3" onClick={closeModal} testId="cancel-action">
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default CreateAnimals;
